import {
  buildCollectionReportSelectionsSummaryJson,
  buildCollectionReportSelectedFeedsTree,
  formatCollectionReportCompareItemLabel,
  formatCollectionReportDate,
  formatCollectionReportDateRange,
  formatCollectionReportRefreshHistoryLabel,
  getCollectionReportSelectedValueAddedProductLabels,
  getCollectionReportSensorDisplayName,
  normalizeCollectionReportSceneLabel,
  resolveCollectionReportCurrentSceneLabel,
} from './collection-report.utils';

type FeedPanelLike = {
  title?: unknown;
};

type SelectionRenderRow = {
  label: string;
  value?: string;
  children?: SelectionRenderRow[];
};

type SelectionRenderSection = {
  title?: string;
  rows: SelectionRenderRow[];
};

export type CollectionReportSelectionsSummaryBuilderInput = {
  collection: any;
  currentUser: any;
  isFeedTasking: boolean;
  isCompare: boolean;
  selectedValue?: any;
  selectionLabel?: any;
  defaultSubdailyCount?: any;
  selectedSubDailyItemsNew?: any[];
  subDailyFinalArray?: any[];
  selectedCheckboxesNew?: any[];
  selectedSceneNew?: any;
  selectedHistory?: Record<string, any> | null;
  scenesNew?: any[];
  filteredCollection?: any;
  leftData?: any[];
  rightData?: any[];
  leftFeedPanels?: Map<string, FeedPanelLike>;
  rightFeedPanels?: Map<string, FeedPanelLike>;
  pastPrediction?: boolean;
};

function uniq(xs: string[]): string[] {
  return Array.from(new Set(xs));
}

function getCompareSceneSelectionPrefix(scene: any): string {
  const operatorKey = String(scene?.operatorKey ?? '').trim().toLowerCase();

  if (operatorKey === 'usl' || operatorKey === 'spire-rf') {
    return 'Collection';
  }

  if (operatorKey === 'axelspace' || operatorKey === 'azista') {
    return 'Tile';
  }

  return 'Scene';
}

function getCompareSceneSelectionNumber(scene: any, rawLabel: string | null, index: number): string {
  const sceneIdx = Number(scene?.sceneIdx);
  if (Number.isFinite(sceneIdx) && sceneIdx > 0) {
    return String(sceneIdx);
  }

  const rawLabelMatch = rawLabel?.match(/^(?:scene|tile|collection)\s+(\d+)$/i);
  if (rawLabelMatch?.[1]) {
    return rawLabelMatch[1];
  }

  return String(index + 1);
}

function getCompareSceneSelectionLabel(scene: any, index: number): string {
  const prefix = getCompareSceneSelectionPrefix(scene);
  const rawLabel = normalizeCollectionReportSceneLabel(
    scene?.name ?? scene?.sceneName ?? scene?.id ?? scene?.scene_id,
  );
  const sceneNumber = getCompareSceneSelectionNumber(scene, rawLabel, index);

  if (rawLabel && /^(scene|tile|collection)\s+\d+$/i.test(rawLabel)) {
    return `${prefix} ${sceneNumber}`;
  }

  return rawLabel || `${prefix} ${sceneNumber}`;
}

function summarizeFeedPanelsForReport(panels: Map<string, FeedPanelLike> | undefined) {
  const titlesRaw: string[] =
    panels instanceof Map
      ? Array.from(panels.values())
          .map((panel) => String(panel?.title || '').trim())
          .filter(Boolean)
      : [];

  return {
    feeds: uniq(titlesRaw),
  };
}

function makeSelectionRow(label: string, value?: unknown, children?: SelectionRenderRow[]): SelectionRenderRow {
  const nextRow: SelectionRenderRow = {
    label: String(label || '').trim() || 'Entry',
  };
  const text = value == null ? '' : String(value).trim();
  if (text) nextRow.value = text;
  if (Array.isArray(children) && children.length) nextRow.children = children;
  return nextRow;
}

function makeIndexedSelectionRows(label: string, values: unknown[]): SelectionRenderRow[] {
  const items = Array.isArray(values)
    ? values
        .map((value) => String(value == null ? '' : value).trim())
        .filter(Boolean)
    : [];

  return items.map((value, index) =>
    makeSelectionRow(items.length > 1 ? `${label} ${index + 1}` : label, value),
  );
}

function getCompareSelectionInfoServices(selection: any): any[] {
  return Array.isArray(selection?.InfoServices)
    ? selection.InfoServices
    : Array.isArray(selection?.informationServices)
      ? selection.informationServices
      : [];
}

function getCheckedCompareScenes(selection: any): any[] {
  return Array.isArray(selection?.scenes)
    ? selection.scenes.filter((scene: any) => scene?.checked === true || scene?.isChecked === true)
    : [];
}

function hasCollectionReportComparePane(pane: unknown): boolean {
  return pane === 'left' || pane === 'right';
}

function sceneEntryHasCollectionReportComparePane(sceneEntry: any): boolean {
  return hasCollectionReportComparePane(
    sceneEntry?.pane ?? sceneEntry?.scene?.pane ?? sceneEntry?.mapPane ?? null,
  );
}

function getComparePaneSortValue(pane: unknown): number {
  return pane === 'left' ? 0 : pane === 'right' ? 1 : 2;
}

function getCheckedCompareFeedLabels(selection: any, paneFeeds: { left: string[]; right: string[] }): string[] {
  const infoServices = getCompareSelectionInfoServices(selection);
  const selectedFeedLabels = infoServices
    .filter((feed: any) => feed?.checked === true)
    .map((feed: any) => String(feed?.feedName ?? feed?.name ?? '').trim())
    .filter(Boolean);
  const paneSpecificFeedLabels =
    selection?.pane === 'left'
      ? paneFeeds.left
      : selection?.pane === 'right'
        ? paneFeeds.right
        : uniq([...(Array.isArray(paneFeeds.left) ? paneFeeds.left : []), ...(Array.isArray(paneFeeds.right) ? paneFeeds.right : [])]);

  return uniq([...selectedFeedLabels, ...paneSpecificFeedLabels]);
}

function shouldIncludeCompareSelection(selection: any): boolean {
  const itemChecked = selection?.checked === true || selection?.isChecked === true;
  const hasCheckedScenes = getCheckedCompareScenes(selection).length > 0;
  const infoServices = getCompareSelectionInfoServices(selection);
  const hasCheckedFeeds = infoServices.some((feed: any) => feed?.checked === true);
  const hasSelectedProducts = getCollectionReportSelectedValueAddedProductLabels(selection).length > 0;

  return itemChecked || hasCheckedScenes || hasCheckedFeeds || hasSelectedProducts;
}

function buildCompareSensorRows(args: {
  items: any[];
  currentUser: any;
  isFeedTasking: boolean;
  paneFeeds: { left: string[]; right: string[] };
}): SelectionRenderRow[] {
  const safeItems = Array.isArray(args.items) ? [...args.items] : [];

  return safeItems
    .filter((item: any) => shouldIncludeCompareSelection(item))
    .sort((a: any, b: any) => getComparePaneSortValue(a?.pane) - getComparePaneSortValue(b?.pane))
    .map((item: any) => {
      const sensorLabel =
        getCollectionReportSensorDisplayName({
          sensor: item,
          currentUser: args.currentUser,
          isFeedTasking: args.isFeedTasking,
        }) || String(item?.name ?? '').trim() || '-';
      const rowLabel = args.isFeedTasking ? 'Question' : 'Sensor';
      const sceneRows = args.isFeedTasking
        ? []
        : Array.isArray(item?.scenes)
          ? item.scenes
              .filter((scene: any) => scene?.checked === true || scene?.isChecked === true)
              .map((scene: any, index: number) => makeSelectionRow('Data', getCompareSceneSelectionLabel(scene, index)))
          : [];
      const feedRows = args.isFeedTasking
        ? []
        : makeIndexedSelectionRows('Feed', getCheckedCompareFeedLabels(item, args.paneFeeds));
      const productRows = makeIndexedSelectionRows(
        'Product',
        getCollectionReportSelectedValueAddedProductLabels(item),
      );

      return makeSelectionRow(rowLabel, sensorLabel, [...sceneRows, ...feedRows, ...productRows]);
    });
}

function getSelectionValueLabel(value: unknown): string {
  const text = value == null ? '' : String(value).trim();
  if (!text) return 'Date';
  if (/^sub-daily/i.test(text)) return 'Sub-daily';
  return text.includes('→') ? 'Date Range' : 'Date';
}

function getRecurrencySelectionBaseLabel(repeatFrequency: unknown): string {
  const text = repeatFrequency == null ? '' : String(repeatFrequency).trim();
  if (!text) return '';

  if (text === 'Daily' || text === 'Sub-daily') {
    return 'Daily Recurrency';
  }

  if (text === 'Monthly' || text === 'GHG-Monthly') {
    return 'Monthly Recurrency';
  }

  return `${text} Recurrency`;
}

function getRecurrencySelectionLabel(args: {
  repeatFrequency: unknown;
  value: unknown;
  index?: number;
  total?: number;
}): string {
  const baseLabel = getRecurrencySelectionBaseLabel(args.repeatFrequency);
  const fallbackLabel = getSelectionValueLabel(args.value);
  const label = baseLabel || fallbackLabel;

  if ((args.total ?? 0) > 1) {
    return `${label} ${(args.index ?? 0) + 1}`;
  }

  return label;
}

export function buildCollectionReportSelectionsSummaryText(
  input: CollectionReportSelectionsSummaryBuilderInput,
): string {
  const feedTree = buildCollectionReportSelectedFeedsTree({
    collection: input.collection,
    currentUser: input.currentUser,
    isFeedTasking: input.isFeedTasking,
  });
  const repeatFrequency = String(input.collection?.repeatFrequency ?? '').trim();
  const selectedValueRaw = input.selectedValue;
  const recurrencyAll = ((input.collection as any)?.recurrencyArray ?? []).filter(
    (item: any) => item?.checked === true || item?.isChecked === true,
  );
  const selectedSubDailyItems = Array.isArray(input.selectedSubDailyItemsNew)
    ? input.selectedSubDailyItemsNew
    : [];
  const subDailyFinalSelections = Array.isArray(input.subDailyFinalArray)
    ? input.subDailyFinalArray
    : [];
  const selectedDateEntries = Array.isArray(input.selectedCheckboxesNew)
    ? input.selectedCheckboxesNew
    : [];
  const hasRecurrencyOptions = Array.isArray((input.collection as any)?.recurrencyArray)
    && ((input.collection as any)?.recurrencyArray?.length ?? 0) > 0;
  const isSingleDateNonCompare = !input.isCompare && (repeatFrequency === 'Daily' || repeatFrequency === 'Sub-daily');
  const isSingleDateRecurrency = repeatFrequency === 'Daily' || repeatFrequency === 'Sub-daily';
  const fallbackRecurrencySelection = !input.isCompare && hasRecurrencyOptions
    ? isSingleDateRecurrency
      ? formatCollectionReportDate(input.selectionLabel ?? selectedValueRaw?.date ?? selectedValueRaw?.start ?? null)
      : selectedValueRaw
        ? formatCollectionReportCompareItemLabel(selectedValueRaw)
        : formatCollectionReportDate(input.selectionLabel ?? null)
    : '';
  const fallbackSubDailySelection =
    repeatFrequency === 'Sub-daily' && input.defaultSubdailyCount != null && input.defaultSubdailyCount !== 0
      ? String(input.defaultSubdailyCount)
      : '';

  const formatDailyRecurrencySelectionLabel = (item: any): string => {
    if (isSingleDateRecurrency) {
      const singleDate = item?.date ?? item?.start ?? selectedValueRaw?.date ?? selectedValueRaw?.start ?? null;
      return formatCollectionReportDate(singleDate);
    }

    return formatCollectionReportCompareItemLabel(item);
  };

  const formatSubDailySelectionLabel = (value: unknown, item?: any): string => {
    const subDailyValue = value == null ? null : String(value).trim();
    if (!subDailyValue) return '-';
    if (isSingleDateNonCompare) {
      return subDailyValue;
    }

    return formatCollectionReportCompareItemLabel({
      subDailyCount: subDailyValue,
      start: item?.start ?? selectedDateEntries[0]?.start ?? selectedValueRaw?.start ?? null,
      end: item?.end ?? selectedDateEntries[0]?.end ?? selectedValueRaw?.end ?? null,
    });
  };

  const getSubDailySelectionValue = (item: any): string | null => {
    if (item == null) return null;
    if (typeof item === 'number') return String(item);
    if (typeof item === 'string') return item.trim() || null;

    const rawValue = item?.value ?? item?.sequenceNumber ?? item?.sequence ?? null;
    if (rawValue == null) return null;

    const text = String(rawValue).trim();
    return text || null;
  };

  const dailyRecurrencySelections = uniq(
    [
      ...(recurrencyAll.length ? recurrencyAll : selectedDateEntries)
        .map((item: any) => formatDailyRecurrencySelectionLabel(item))
        .filter((label: string) => !!label && label !== '-' && label !== 'Selection'),
      ...(fallbackRecurrencySelection && fallbackRecurrencySelection !== '-' ? [fallbackRecurrencySelection] : []),
    ],
  ).slice(0, 5);

  const subDailySelections = input.isCompare
    ? []
    : uniq(
        (selectedSubDailyItems.length ? selectedSubDailyItems : subDailyFinalSelections)
          .map((item: any) => formatSubDailySelectionLabel(getSubDailySelectionValue(item), item))
          .filter((label: string) => !!label && label !== '-' && label !== 'Selection')
          .concat(fallbackSubDailySelection ? [fallbackSubDailySelection] : []),
      )
        .filter((label: string) => !!label && label !== '-' && label !== 'Selection')
        .slice(0, 5);

  const currentSceneRaw = input.selectedSceneNew ?? null;
  const currentScene = currentSceneRaw
    ? {
        id: currentSceneRaw?.id ?? currentSceneRaw?.scene_id ?? null,
        label:
          normalizeCollectionReportSceneLabel(currentSceneRaw?.sceneName ?? currentSceneRaw?.name ?? currentSceneRaw?.id) || null,
      }
    : null;

  const selectedHistoryObj = input.selectedHistory && typeof input.selectedHistory === 'object' ? input.selectedHistory : {};
  const selectedHistoryIds = Object.keys(selectedHistoryObj);
  const refreshSelectedRaw: Array<{ refreshId: string; dateTime: any }> = [];
  const sensorsAll = Array.isArray(input.collection?.sensors) ? input.collection.sensors : [];
  const sensorsWithRefreshSelected: any[] = [];

  for (const sensor of sensorsAll) {
    const refreshHistory = Array.isArray(sensor?.refreshHistory) ? sensor.refreshHistory : [];
    for (const item of refreshHistory) {
      const refreshId = item?.refreshId != null ? String(item.refreshId) : '';
      if (!refreshId) continue;

      const checked = item?.checked === true || item?.isChecked === true;
      const selectedByData = selectedHistoryIds.includes(refreshId);
      if (!checked && !selectedByData) continue;

      if (!sensorsWithRefreshSelected.includes(sensor)) sensorsWithRefreshSelected.push(sensor);
      const dateTime =
        item?.name ??
        item?.refreshDateTime ??
        item?.datetime ??
        item?.dateTime ??
        item?.timestamp ??
        item?.createdAt ??
        item?.time ??
        item?.date ??
        null;
      refreshSelectedRaw.push({ refreshId, dateTime });
    }
  }

  const refreshSelectedUniq = Array.from(new Map(refreshSelectedRaw.map((item) => [item.refreshId, item])).values());
  const checkedScenesRaw = sensorsAll
    .filter((sensor: any) => sensor?.checked === true || sensor?.isChecked === true)
    .flatMap((sensor: any) => (Array.isArray(sensor?.scenes) ? sensor.scenes : []))
    .filter((scene: any) => scene?.checked === true || scene?.isChecked === true)
    .map((scene: any) => normalizeCollectionReportSceneLabel(scene?.sceneName ?? scene?.name ?? scene?.id))
    .filter(Boolean);
  const checkedScenes = uniq(checkedScenesRaw).slice(0, 5);

  const historySceneLabel = (() => {
    for (const sensor of sensorsWithRefreshSelected) {
      const firstScene = Array.isArray(sensor?.scenes) && sensor.scenes.length ? sensor.scenes[0] : null;
      const label = normalizeCollectionReportSceneLabel(firstScene?.sceneName ?? firstScene?.name ?? firstScene?.id);
      if (label) return label;
    }
    return null;
  })();

  const subDailyItemsWithPane = selectedSubDailyItems
    .map((item: any) => ({
      value: item?.value ?? item,
      pane: item?.mapPane ?? item?.pane ?? null,
      siteName: item?.siteName ?? null,
    }))
    .filter((item: any) => item?.value != null && (item?.pane === 'left' || item?.pane === 'right'));
  const hasCompareDataLists =
    (Array.isArray(input.leftData) && input.leftData.length > 0)
    || (Array.isArray(input.rightData) && input.rightData.length > 0);
  const hasCompareScenePanes = Array.isArray(input.scenesNew)
    ? input.scenesNew.some((scene: any) => sceneEntryHasCollectionReportComparePane(scene))
    : false;
  const useCompareSummary = input.isCompare && (
    selectedDateEntries.length > 0
    || subDailyFinalSelections.length > 0
    || subDailyItemsWithPane.length > 0
    || hasCompareDataLists
    || hasCompareScenePanes
  );
  const subDailyCompareEnabled = useCompareSummary && subDailyItemsWithPane.length >= 2;
  const subDailyLeft = subDailyItemsWithPane.find((item: any) => item.pane === 'left') ?? null;
  const subDailyRight = subDailyItemsWithPane.find((item: any) => item.pane === 'right') ?? null;

  const comparePanelFeeds = {
    left: summarizeFeedPanelsForReport(input.leftFeedPanels).feeds,
    right: summarizeFeedPanelsForReport(input.rightFeedPanels).feeds,
  };
  const compareSelectionItems = useCompareSummary
    ? (repeatFrequency === 'Sub-daily' && subDailyFinalSelections.length ? subDailyFinalSelections : selectedDateEntries)
    : [];
  const compareSensorSourceItems = useCompareSummary
    ? (compareSelectionItems.length
        ? compareSelectionItems
        : sensorsAll.filter((sensor: any) => shouldIncludeCompareSelection(sensor)))
    : [];
  const compareSensorRows = useCompareSummary
    ? buildCompareSensorRows({
        items: compareSensorSourceItems,
        currentUser: input.currentUser,
        isFeedTasking: input.isFeedTasking,
        paneFeeds: comparePanelFeeds,
      })
    : [];

  const currentSceneLabel =
    currentScene?.label || historySceneLabel || checkedScenes.length
      ? resolveCollectionReportCurrentSceneLabel({
          currentSceneLabel: currentScene?.label,
          historySceneLabel,
          checkedScenes,
        })
      : '';
  const refreshHistoryLabels = refreshSelectedUniq.map((item) =>
    formatCollectionReportRefreshHistoryLabel({
      dateTime: item?.dateTime,
      refreshId: item?.refreshId,
    }),
  );
  const compareSelectionLabels = useCompareSummary ? dailyRecurrencySelections : [];
  const compareSubDailySelectionRows = useCompareSummary
    ? [...subDailyItemsWithPane]
        .sort((a: any, b: any) => getComparePaneSortValue(a?.pane) - getComparePaneSortValue(b?.pane))
        .map((item: any) =>
          makeSelectionRow(
            item?.siteName ? 'Site Name' : 'Sub-daily Count',
            item?.siteName ? item.siteName : String(item?.value ?? '').trim(),
          ),
        )
        .filter((row: SelectionRenderRow) => !!row.value)
    : [];
  const hasFeedTreeSensors = Array.isArray(feedTree?.s) && feedTree.s.length > 0;
  const sections: SelectionRenderSection[] = [];

  if (useCompareSummary && (compareSelectionLabels.length || compareSubDailySelectionRows.length)) {
    const compareDateRows = compareSelectionLabels.map((selection, index) =>
      makeSelectionRow(
        getRecurrencySelectionLabel({
          repeatFrequency,
          value: selection,
          index,
          total: compareSelectionLabels.length,
        }),
        selection,
      ),
    );

    sections.push({
      rows: [
        ...compareDateRows,
        ...compareSubDailySelectionRows,
      ],
    });
  }

  if (useCompareSummary) {
    sections.push({
      rows: [makeSelectionRow('Comparison', 'Enabled')],
    });
  }

  if (useCompareSummary && compareSensorRows.length) {
    sections.push({
      title: compareSensorRows.length > 1 ? (input.isFeedTasking ? 'Questions' : 'Sensors') : undefined,
      rows: compareSensorRows,
    });
  }

  if (!useCompareSummary && dailyRecurrencySelections.length) {
    sections.push({
      rows: dailyRecurrencySelections.map((selection, index) =>
        makeSelectionRow(
          getRecurrencySelectionLabel({
            repeatFrequency,
            value: selection,
            index,
            total: dailyRecurrencySelections.length,
          }),
          selection,
        ),
      ),
    });
  }

  if (!useCompareSummary && subDailySelections.length) {
    sections.push({
      rows: subDailySelections.map((selection) => makeSelectionRow('Sub-daily Count', selection)),
    });
  }

  if (!useCompareSummary && currentSceneLabel && !hasFeedTreeSensors) {
    sections.push({
      rows: [makeSelectionRow('Data', currentSceneLabel)],
    });
  }

  if (refreshHistoryLabels.length) {
    sections.push({
      title: 'Refresh History',
      rows: refreshHistoryLabels.map((entry) => makeSelectionRow('Refresh', entry)),
    });
  }

  if (input.pastPrediction) {
    sections.push({
      rows: [makeSelectionRow('Past Predictions', 'Enabled')],
    });
  }

  if (!useCompareSummary && hasFeedTreeSensors) {
    const sensorGroupLabel = input.isFeedTasking ? 'Question' : 'Sensor';
    const feedTreeSensorRows = feedTree.s.map((sensor: any) =>
      makeSelectionRow(
        sensorGroupLabel,
        sensor?.n,
        [
          ...(!input.isFeedTasking && Array.isArray(sensor?.sc) && sensor.sc.length
            ? sensor.sc.map((scene: any) =>
                makeSelectionRow(
                  'Data',
                  scene?.n,
                  Array.isArray(scene?.f)
                    ? makeIndexedSelectionRows('Feed', scene.f)
                    : [],
                ),
              )
            : []),
          ...(Array.isArray(sensor?.p)
            ? makeIndexedSelectionRows('Product', sensor.p)
            : []),
        ],
      ),
    );

    sections.push({
      title: feedTreeSensorRows.length > 1 ? (input.isFeedTasking ? 'Questions' : 'Sensors') : undefined,
      rows: feedTreeSensorRows,
    });
  }

  if (feedTree?.t) {
    sections.push({
      rows: [makeSelectionRow('Notice', '(Selection list truncated)')],
    });
  }

  return buildCollectionReportSelectionsSummaryJson({
    sections,
  });
}